import React from 'react';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import IcecreamIcon from '@mui/icons-material/Icecream';
import EmojiFoodBeverageIcon from '@mui/icons-material/EmojiFoodBeverage';
import TapasIcon from '@mui/icons-material/Tapas';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HikingIcon from '@mui/icons-material/Hiking';
import MuseumIcon from '@mui/icons-material/Museum';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import SportsIcon from '@mui/icons-material/Sports';
import ParkIcon from '@mui/icons-material/Park';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StarIcon from '@mui/icons-material/Star';
import PlaceIcon from '@mui/icons-material/Place';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';

type IconSize = 'small' | 'medium' | 'large' | 'inherit';

interface IconMapping {
  keywords: string[];
  icon: React.ComponentType<{ fontSize?: IconSize }>;
}

// Travel mode mappings
const travelModeMappings: IconMapping[] = [
  { keywords: ['plane', 'flight', 'air', 'fly'], icon: FlightIcon },
  { keywords: ['train', 'rail'], icon: TrainIcon },
  { keywords: ['bus', 'coach'], icon: DirectionsBusIcon },
  { keywords: ['boat', 'ferry', 'cruise', 'ship'], icon: DirectionsBoatIcon },
  { keywords: ['car', 'drive', 'road'], icon: DirectionsCarIcon },
];

// Cuisine type mappings
const cuisineMappings: IconMapping[] = [
  { keywords: ['italian', 'pizza', 'pasta'], icon: LocalPizzaIcon },
  { keywords: ['asian', 'chinese', 'japanese', 'ramen', 'thai', 'vietnamese', 'korean', 'sushi'], icon: RamenDiningIcon },
  { keywords: ['french', 'pastry', 'bakery', 'boulangerie'], icon: BakeryDiningIcon },
  { keywords: ['american', 'burger', 'bbq', 'steak'], icon: LunchDiningIcon },
  { keywords: ['dessert', 'sweet', 'ice cream', 'gelato'], icon: IcecreamIcon },
  { keywords: ['spanish', 'tapas', 'mediterranean', 'greek'], icon: TapasIcon },
  { keywords: ['mexican', 'indian', 'middle eastern', 'turkish', 'lebanese'], icon: DinnerDiningIcon },
  { keywords: ['cafe', 'coffee', 'tea'], icon: EmojiFoodBeverageIcon },
  { keywords: ['bar', 'cocktail', 'wine'], icon: LocalBarIcon },
];

// Activity type mappings
const activityMappings: IconMapping[] = [
  { keywords: ['hiking', 'adventure', 'trek', 'climbing', 'outdoor'], icon: HikingIcon },
  { keywords: ['museum', 'culture', 'art', 'gallery', 'history'], icon: MuseumIcon },
  { keywords: ['beach', 'swim', 'ocean', 'sea', 'coast'], icon: BeachAccessIcon },
  { keywords: ['shop', 'market', 'mall', 'boutique'], icon: ShoppingBagIcon },
  { keywords: ['nightlife', 'bar', 'club', 'party'], icon: NightlifeIcon },
  { keywords: ['sport', 'game', 'stadium', 'match'], icon: SportsIcon },
  { keywords: ['nature', 'park', 'wildlife', 'garden', 'forest'], icon: ParkIcon },
  { keywords: ['theater', 'show', 'entertainment', 'concert', 'performance'], icon: TheaterComedyIcon },
  { keywords: ['photo', 'sightsee', 'view', 'landmark', 'tour'], icon: CameraAltIcon },
];

/**
 * Find matching icon from mappings based on keywords
 */
function findMatchingIcon(
  value: string,
  mappings: IconMapping[],
  defaultIcon: React.ComponentType<{ fontSize?: IconSize }>
): React.ComponentType<{ fontSize?: IconSize }> {
  const valueLower = value.toLowerCase();

  for (const mapping of mappings) {
    if (mapping.keywords.some(keyword => valueLower.includes(keyword))) {
      return mapping.icon;
    }
  }

  return defaultIcon;
}

/**
 * Get icon element for a travel mode
 * @param mode - Travel mode string (e.g., "flight", "train", "bus")
 * @param fontSize - Icon size (default: 'medium')
 * @returns React element with the appropriate icon
 */
export function getTravelModeIcon(mode: string, fontSize: IconSize = 'medium'): React.ReactElement {
  const IconComponent = findMatchingIcon(mode, travelModeMappings, DirectionsCarIcon);
  return React.createElement(IconComponent, { fontSize });
}

/**
 * Get icon element for a cuisine type
 * @param cuisine - Cuisine type string (e.g., "Italian", "Asian")
 * @param fontSize - Icon size (default: 'small')
 * @returns React element with the appropriate icon
 */
export function getCuisineIcon(cuisine: string, fontSize: IconSize = 'small'): React.ReactElement {
  const IconComponent = findMatchingIcon(cuisine, cuisineMappings, RestaurantIcon);
  return React.createElement(IconComponent, { fontSize });
}

/**
 * Get icon element for an activity type
 * @param activity - Activity type string (e.g., "hiking", "museum")
 * @param fontSize - Icon size (default: 'small')
 * @returns React element with the appropriate icon
 */
export function getActivityIcon(activity: string, fontSize: IconSize = 'small'): React.ReactElement {
  const IconComponent = findMatchingIcon(activity, activityMappings, StarIcon);
  return React.createElement(IconComponent, { fontSize });
}

/**
 * Get icon component (not element) for a travel mode
 * Useful when you need to pass props dynamically
 */
export function getTravelModeIconComponent(mode: string): React.ComponentType<{ fontSize?: IconSize }> {
  return findMatchingIcon(mode, travelModeMappings, DirectionsCarIcon);
}

/**
 * Get icon component for a cuisine type
 */
export function getCuisineIconComponent(cuisine: string): React.ComponentType<{ fontSize?: IconSize }> {
  return findMatchingIcon(cuisine, cuisineMappings, RestaurantIcon);
}

/**
 * Get icon component for an activity type
 */
export function getActivityIconComponent(activity: string): React.ComponentType<{ fontSize?: IconSize }> {
  return findMatchingIcon(activity, activityMappings, StarIcon);
}

// Re-export commonly used icons for convenience
export {
  FlightIcon,
  TrainIcon,
  DirectionsBusIcon,
  DirectionsBoatIcon,
  DirectionsCarIcon,
  RestaurantIcon,
  PlaceIcon,
  LocalBarIcon,
  LocalCafeIcon,
  MuseumIcon,
  BeachAccessIcon,
  ParkIcon,
  StarIcon,
};
